import type {
  CreateTicketConfigPayload,
  ITicketConfigDetail,
  UpdateTicketConfigPayload,
} from '@/shared/api/types/ticket-config-types'
import { z } from 'zod'

export const ticketConfigFormSchema = z.object({
  ticketType: z.enum(['free', 'paid']),
  localizedData: z.object({
    en: z.object({
      name: z.string().trim().min(1, 'ticketConfig.validation.englishNameRequired'),
      description: z.string().optional(),
    }),
    'vi-south': z.object({
      name: z.string().trim().min(1, 'ticketConfig.validation.vietnameseNameRequired'),
      description: z.string().optional(),
    }),
  }),
  ticketConfigFeatureIds: z.array(z.string()),
})

export type TicketConfigFormValues = z.infer<typeof ticketConfigFormSchema>

export const EMPTY_TICKET_CONFIG_VALUES: TicketConfigFormValues = {
  ticketType: 'free',
  localizedData: {
    en: { name: '', description: '' },
    'vi-south': { name: '', description: '' },
  },
  ticketConfigFeatureIds: [],
}

type TicketConfigLocalizedData = NonNullable<ITicketConfigDetail['localizedData']>
type TicketConfigLocalizedField = 'name' | 'description'

function normalizeLanguageCode(languageCode: string): string {
  return languageCode.trim().toLowerCase().replace(/_/g, '-')
}

function resolveLocalizedEntry(
  localizedData: ITicketConfigDetail['localizedData'],
  languageCode: 'en' | 'vi-south',
): TicketConfigLocalizedData[string] | undefined {
  if (!localizedData) return undefined

  const entries = Object.entries(localizedData).map(([key, value]) => ({
    key: normalizeLanguageCode(key),
    value,
  }))
  const normalizedLanguageCode = normalizeLanguageCode(languageCode)
  const exact = entries.find((entry) => entry.key === normalizedLanguageCode)?.value
  if (exact) return exact

  if (languageCode === 'vi-south') {
    return (
      entries.find((entry) => entry.key === 'vi')?.value ??
      entries.find((entry) => entry.key.startsWith('vi-'))?.value
    )
  }

  return undefined
}

export function getTicketConfigLocalizedValue(
  localizedData: ITicketConfigDetail['localizedData'],
  languageCode: 'en' | 'vi-south',
  field: TicketConfigLocalizedField,
): string {
  return resolveLocalizedEntry(localizedData, languageCode)?.[field] ?? ''
}

export function ticketConfigToFormValues(config: ITicketConfigDetail): TicketConfigFormValues {
  return {
    ticketType: config.ticketType,
    localizedData: {
      en: {
        name: getTicketConfigLocalizedValue(config.localizedData, 'en', 'name'),
        description: getTicketConfigLocalizedValue(config.localizedData, 'en', 'description'),
      },
      'vi-south': {
        name: getTicketConfigLocalizedValue(config.localizedData, 'vi-south', 'name'),
        description: getTicketConfigLocalizedValue(config.localizedData, 'vi-south', 'description'),
      },
    },
    ticketConfigFeatureIds: config.ticketConfigFeature.map((item) => item.featureId),
  }
}

export function formValuesToCreatePayload(
  values: TicketConfigFormValues,
): CreateTicketConfigPayload {
  return {
    type: values.ticketType,
    localizedData: {
      en: {
        name: values.localizedData.en.name.trim(),
        description: values.localizedData.en.description?.trim() ?? '',
      },
      'vi-south': {
        name: values.localizedData['vi-south'].name.trim(),
        description: values.localizedData['vi-south'].description?.trim() ?? '',
      },
    },
    ticketConfigFeatureIds: values.ticketConfigFeatureIds,
  }
}

export function basicInfoToUpdatePayload(
  basicInfo: Pick<TicketConfigFormValues, 'ticketType' | 'localizedData'>,
): Pick<UpdateTicketConfigPayload, 'type' | 'localizedData'> {
  return {
    type: basicInfo.ticketType,
    localizedData: {
      en: {
        name: basicInfo.localizedData.en.name.trim(),
        description: basicInfo.localizedData.en.description?.trim() ?? '',
      },
      'vi-south': {
        name: basicInfo.localizedData['vi-south'].name.trim(),
        description: basicInfo.localizedData['vi-south'].description?.trim() ?? '',
      },
    },
  }
}

export function featureIdsToUpdatePayload(
  featureIds: string[],
): Pick<UpdateTicketConfigPayload, 'ticketConfigFeatureIds'> {
  return { ticketConfigFeatureIds: featureIds }
}
